// Partner Hub queue: ordering, station tabs, the single next action, new-order detection.

/** @typedef {import('@popcart/core/v1').ShopQueueItem} ShopQueueItem */
/** @typedef {import('@popcart/core/v1').ProductLine} ProductLine */
/** @typedef {import('@popcart/core/v1').ShopOrderStatus} ShopOrderStatus */

/** @type {ProductLine[]} */
export const LINES = ['fresh', 'grill', 'mart'];

/** Station wording from the Partner Hub mockup. */
export const STATIONS = {
  fresh: { line: 'Fresh', tab: 'Weigh', station: 'Fresh · weigh and pack' },
  grill: { line: 'Grill', tab: 'Kitchen', station: 'Grill · kitchen' },
  mart: { line: 'Grocery', tab: 'Pick', station: 'Grocery · pick' },
};

const ACTIONABLE = ['placed', 'accepted', 'preparing'];

/** @param {ShopOrderStatus} s */
function group(s) {
  if (ACTIONABLE.includes(s)) return 0;
  if (s === 'ready') return 1;
  return 2;
}

/**
 * Work the shop still has to do first, Express on top, then oldest first.
 * Ready orders (waiting for the rider) sink below; finished ones sink last.
 * @param {ShopQueueItem[]} items
 */
export function sortQueue(items) {
  return items.slice().sort((a, b) => {
    const g = group(a.status) - group(b.status);
    if (g) return g;
    if (a.speed !== b.speed) return a.speed === 'express' ? -1 : 1;
    if (a.speed === 'express' && a.expressDeadline && b.expressDeadline && a.expressDeadline !== b.expressDeadline) {
      return a.expressDeadline < b.expressDeadline ? -1 : 1;
    }
    if (a.placedAt !== b.placedAt) return a.placedAt < b.placedAt ? -1 : 1;
    return a.code < b.code ? -1 : a.code > b.code ? 1 : 0;
  });
}

/**
 * @param {ShopQueueItem[]} items
 * @param {ProductLine | 'all'} line
 */
export function filterByLine(items, line) {
  if (line === 'all') return items;
  return items.filter((i) => i.lines.includes(line) || i.items.some((x) => x.line === line));
}

/** Tab counts: all + per line. @param {ShopQueueItem[]} items */
export function lineCounts(items) {
  /** @type {Record<ProductLine | 'all', number>} */
  const c = { all: items.length, fresh: 0, grill: 0, mart: 0 };
  for (const l of LINES) c[l] = filterByLine(items, l).length;
  return c;
}

/**
 * The one forward step a shop can take from this status (the screen's one primary button).
 * @param {ShopOrderStatus} status
 * @returns {{ to: 'accepted' | 'preparing' | 'ready'; label: string } | null}
 */
export function nextAction(status) {
  if (status === 'placed') return { to: 'accepted', label: 'Accept order' };
  if (status === 'accepted') return { to: 'preparing', label: 'Start preparing' };
  if (status === 'preparing') return { to: 'ready', label: 'Ready for rider' };
  return null;
}

/** @param {ShopOrderStatus} status */
export function canCancel(status) {
  return status === 'placed' || status === 'accepted';
}

/**
 * Status stickers (§09 / §14 badge vocabulary). NEW is the Pop Yellow sticker with an Ink label;
 * work in hand (accepted, preparing) is a Cream sticker, so nothing but the one primary button is
 * Cart Blue; the functional colours stay for ready (success) and cancelled (error).
 * @type {Record<ShopOrderStatus, { label: string; tone: 'new' | 'success' | 'warning' | 'error' | 'info' | 'neutral' }>}
 */
export const STATUS_VIEW = {
  placed: { label: 'New', tone: 'new' },
  accepted: { label: 'Accepted', tone: 'neutral' },
  preparing: { label: 'Preparing', tone: 'neutral' },
  ready: { label: 'Ready', tone: 'success' },
  picked_up: { label: 'Picked up', tone: 'neutral' },
  delivered: { label: 'Delivered', tone: 'neutral' },
  cancelled: { label: 'Cancelled', tone: 'error' },
};

/**
 * The order's items split by station, in Fresh, Grill, Grocery order; empty stations dropped.
 * @param {ShopQueueItem} item
 */
export function itemsByStation(item) {
  return LINES.map((line) => ({ line, title: STATIONS[line].station, items: item.items.filter((x) => x.line === line) })).filter(
    (g) => g.items.length > 0,
  );
}

/** "10 pork BBQ, 2 rice" (first few items). @param {ShopQueueItem} item @param {number} [max] */
export function itemSummary(item, max = 3) {
  const parts = item.items.slice(0, max).map((x) => `${x.qty} ${x.name}`);
  const more = item.items.length - max;
  return parts.join(', ') + (more > 0 ? ` +${more} more` : '');
}

/**
 * New orders since the last poll: ids in `placed` now that weren't seen before. The first load
 * (prev === null) never alerts, so opening the screen is quiet.
 * @param {Set<string> | null} prev ids seen so far
 * @param {ShopQueueItem[]} items
 * @returns {{ fresh: ShopQueueItem[]; seen: Set<string> }}
 */
export function detectNewOrders(prev, items) {
  const seen = new Set(prev || []);
  /** @type {ShopQueueItem[]} */
  const fresh = [];
  for (const i of items) {
    if (!seen.has(i.shopOrderId)) {
      if (prev && i.status === 'placed') fresh.push(i);
      seen.add(i.shopOrderId);
    }
  }
  return { fresh, seen };
}

/** Oldest open order's wait in minutes, or null. @param {ShopQueueItem[]} items */
export function oldestWaiting(items) {
  const open = items.filter((i) => ACTIONABLE.includes(i.status));
  if (!open.length) return null;
  return Math.max(...open.map((i) => i.minutesWaiting));
}
